import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ForcedPasswordChangeForm } from '@react-apps/auth';
import { AuthLayout } from '@react-apps/design-system';
import { firebaseAuth } from '@/lib/firebase';
import { useChangePassword } from '../hooks/use-change-password';

/**
 * Tela de troca de senha OBRIGATÓRIA (força-troca / senha temporária).
 *
 * Chega aqui quando `POST /auth/sign-in` recusou com 403 `PASSWORD_CHANGE_REQUIRED`:
 * o login no Firebase deu certo (a senha temporária é válida), só a resolução de
 * perfil foi barrada. Rota **fora** do AuthGuard do app — a pessoa não tem perfil
 * resolvido. O e-mail e a senha temporária chegam pelo `state` do router (nunca
 * na URL).
 *
 * Só o caminho com senha (`signIn`) chega aqui, porque só ele tem a temporária
 * em mão. Token e Google param no `useSignIn`, com a sessão derrubada e a
 * mensagem pedindo a entrada por e-mail + temporária — mandá-los para cá fazia
 * esta tela reencaminhar ao `/sign-in`, que entrava sozinho e batia no mesmo
 * 403, em laço.
 *
 * Reusa `useChangePassword`, que reautentica com a senha atual (a temporária),
 * troca, e faz o **re-login silencioso** com a senha nova — importante porque
 * trocar a senha revoga todas as sessões, inclusive a que o suporte pode ter
 * aberto com a temporária.
 */
export function ChangePasswordRequiredPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as {
    email?: string;
    tempPassword?: string;
  } | null;
  const email = state?.email ?? '';
  const tempPassword = state?.tempPassword ?? '';

  const { changePassword, isLoading, error, success } = useChangePassword();

  // Re-login silencioso já aconteceu dentro do hook → segue para o app.
  useEffect(() => {
    if (success) void navigate('/profile', { replace: true });
  }, [success, navigate]);

  // Sobra o acesso direto à URL: sem sessão Firebase ou sem a temporária não há
  // o que reautenticar. Volta ao login COM o recado (a `SignInPage` mostra o
  // `state.message`) em vez de despejar a pessoa num formulário vazio e mudo.
  if (!firebaseAuth.currentUser || !tempPassword) {
    return (
      <Navigate
        to="/sign-in"
        replace
        state={{
          message:
            'Entre com seu e-mail e a senha temporária para definir uma nova senha.',
        }}
      />
    );
  }

  return (
    <AuthLayout
      title="Defina uma nova senha"
      subtitle={
        email
          ? `Para continuar, ${email} precisa trocar a senha temporária por uma senha própria.`
          : 'Para continuar, troque a senha temporária por uma senha própria.'
      }
    >
      <ForcedPasswordChangeForm
        tempPassword={tempPassword}
        isLoading={isLoading}
        error={error}
        onSubmit={changePassword}
      />
    </AuthLayout>
  );
}
